import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Connection state
  isConnected: false,
  isAuthenticated: false,
  
  // Chat widget state
  isWidgetOpen: false,
  widgetView: 'conversations', // 'conversations' or 'chat'
  activeConversationId: null,
  
  // Data
  conversations: [],
  messages: {},
  unreadCount: 0,
  
  // UI state
  isLoading: false,
  isSending: false,
  error: null,
  
  // Pagination
  conversationsPagination: {
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false
  },
  messagesPagination: {},
  
  // Search and filters
  searchQuery: '',
  notifications: {
    enabled: true,
    sound: true
  }
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    // Connection actions
    setConnectionStatus: (state, action) => {
      state.isConnected = action.payload;
    },
    
    setAuthenticationStatus: (state, action) => {
      state.isAuthenticated = action.payload;
    },
    
    // Widget actions
    setWidgetOpen: (state, action) => {
      state.isWidgetOpen = action.payload;
    },
    
    setWidgetView: (state, action) => {
      state.widgetView = action.payload;
    },
    
    setActiveConversation: (state, action) => {
      state.activeConversationId = action.payload;
      if (action.payload) {
        state.widgetView = 'chat';
      }
    },
    
    closeActiveConversation: (state) => {
      state.activeConversationId = null;
      state.widgetView = 'conversations';
    },
    
    // Data actions
    setConversations: (state, action) => {
      state.conversations = action.payload.conversations || [];
      state.conversationsPagination = action.payload.pagination || initialState.conversationsPagination;
      
      // Recalculate global unread count from individual conversations
      state.unreadCount = state.conversations.reduce((total, conversation) => {
        return total + (conversation.unreadCount || 0);
      }, 0);
    },
    
    addConversation: (state, action) => {
      const conversation = action.payload;
      const existingIndex = state.conversations.findIndex(c => c._id === conversation._id);
      
      if (existingIndex >= 0) {
        state.conversations[existingIndex] = conversation;
      } else {
        state.conversations.unshift(conversation);
      }
      
      // Sort by last message timestamp
      state.conversations.sort((a, b) => 
        new Date(b.lastMessageAt) - new Date(a.lastMessageAt)
      );
    },
    
    updateConversation: (state, action) => {
      const { conversationId, updates } = action.payload;
      const conversation = state.conversations.find(c => c._id === conversationId);
      
      if (conversation) {
        Object.assign(conversation, updates);
      }
    },
    
    setMessages: (state, action) => {
      const { conversationId, messages, pagination } = action.payload;
      state.messages[conversationId] = messages || [];
      state.messagesPagination[conversationId] = pagination || {};
    },
    
    addMessage: (state, action) => {
      const { conversationId, message } = action.payload;
      
      if (!state.messages[conversationId]) {
        state.messages[conversationId] = [];
      }
      
      // Check if message already exists (prevent duplicates)
      const existingMessage = state.messages[conversationId].find(m => m._id === message._id);
      if (!existingMessage) {
        state.messages[conversationId].push(message);
        
        // Sort messages by timestamp
        state.messages[conversationId].sort((a, b) => 
          new Date(a.createdAt) - new Date(b.createdAt)
        );
      }
      
      // Update conversation in list
      const conversation = state.conversations.find(c => c._id === conversationId);
      if (conversation) {
        conversation.lastMessage = message;
        conversation.lastMessageAt = message.createdAt;
        
        // Increment unread count for incoming messages
        if (message.direction === 'incoming' && !message.isRead) {
          conversation.unreadCount = (conversation.unreadCount || 0) + 1;
          // Also increment global unread count
          state.unreadCount = (state.unreadCount || 0) + 1;
        }
        
        // Move conversation to top
        const index = state.conversations.findIndex(c => c._id === conversationId);
        if (index > 0) {
          const [conv] = state.conversations.splice(index, 1);
          state.conversations.unshift(conv);
        }
      }
    },
    
    updateMessage: (state, action) => {
      const { conversationId, messageId, updates } = action.payload;
      
      if (state.messages[conversationId]) {
        const message = state.messages[conversationId].find(m => m._id === messageId);
        if (message) {
          Object.assign(message, updates);
        }
      }
    },
    
    markMessagesAsRead: (state, action) => {
      const { conversationId, messageIds } = action.payload;
      
      if (state.messages[conversationId]) {
        state.messages[conversationId].forEach(message => {
          if (messageIds.includes(message._id)) {
            message.isRead = true;
            message.readAt = new Date().toISOString();
          }
        });
      }
      
      // Update conversation unread count
      const conversation = state.conversations.find(c => c._id === conversationId);
      if (conversation) {
        const previousUnread = conversation.unreadCount || 0;
        conversation.unreadCount = Math.max(0, previousUnread - messageIds.length);
        
        // Update global unread count
        state.unreadCount = Math.max(0, state.unreadCount - messageIds.length);
      }
    },
    
    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },
    
    // UI state actions
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    
    setSending: (state, action) => {
      state.isSending = action.payload;
    },
    
    setError: (state, action) => {
      state.error = action.payload;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    
    // Notification settings
    updateNotificationSettings: (state, action) => {
      state.notifications = { ...state.notifications, ...action.payload };
    },
    
    // Reset state
    resetChatState: (state) => {
      return { ...initialState };
    }
  }
});

export const {
  setConnectionStatus,
  setAuthenticationStatus,
  setWidgetOpen,
  setWidgetView,
  setActiveConversation,
  closeActiveConversation,
  setConversations,
  addConversation,
  updateConversation,
  setMessages,
  addMessage,
  updateMessage,
  markMessagesAsRead,
  setUnreadCount,
  setLoading,
  setSending,
  setError,
  clearError,
  setSearchQuery,
  updateNotificationSettings,
  resetChatState
} = chatSlice.actions;

// Selectors
export const selectChatState = (state) => state.chat;
export const selectIsConnected = (state) => state.chat.isConnected;
export const selectIsAuthenticated = (state) => state.chat.isAuthenticated;
export const selectIsWidgetOpen = (state) => state.chat.isWidgetOpen;
export const selectWidgetView = (state) => state.chat.widgetView;
export const selectActiveConversationId = (state) => state.chat.activeConversationId;
export const selectConversations = (state) => state.chat.conversations;
export const selectUnreadCount = (state) => state.chat.unreadCount;
export const selectCalculatedUnreadCount = (state) => {
  // Calculate total unread count from individual conversations
  return state.chat.conversations.reduce((total, conversation) => {
    return total + (conversation.unreadCount || 0);
  }, 0);
};
export const selectActiveConversation = (state) => {
  const activeId = state.chat.activeConversationId;
  return activeId ? state.chat.conversations.find(c => c._id === activeId) : null;
};
export const selectActiveMessages = (state) => {
  const activeId = state.chat.activeConversationId;
  return activeId ? state.chat.messages[activeId] || [] : [];
};
export const selectIsLoading = (state) => state.chat.isLoading;
export const selectIsSending = (state) => state.chat.isSending;
export const selectError = (state) => state.chat.error;
export const selectSearchQuery = (state) => state.chat.searchQuery;
export const selectNotificationSettings = (state) => state.chat.notifications;

export default chatSlice.reducer;